import { Client } from 'urql'
import {
  Mutation_Root,
  Principle_Config,
  Query_Root,
  Subprinciple_Config_Insert_Input,
  Subprinciple_Config_Set_Input,
} from 'src/@types/graphql'
import { queryAllPrincipleConfigs } from 'src/screens/administration/configuration-management/principles-configuration/principles/principlesConfigQueries'
import { SupportedLocale } from 'src/@types'
import { Option } from 'src/shared/form/control'
import {
  insertSubprincipleConfig,
  updateSubprincipleConfigById,
} from 'src/screens/administration/configuration-management/principles-configuration/subprinciples/subprinciplesConfigQueries'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'

export const canUpdateSubprincipleStatus = (
  selectedParentPrincipleId: number | null,
  selectedStatus: 'ACTIVE' | 'INACTIVE',
  parentPrincipleOptions: Option[],
): boolean => {
  const isActiveSelected = selectedStatus === CONFIGURATION_STATUS.ACTIVE

  if (isActiveSelected && selectedParentPrincipleId != null) {
    return parentPrincipleOptions.some((ppOpt) => ppOpt.value === selectedParentPrincipleId && !ppOpt.disabled)
  }

  return true
}

export const mapFetchedPrincipleConfigsToOptions = (
  fetchedPrincipleConfigs: Principle_Config[],
  getMessage: (key: string, params?: Array<string | number>) => string,
  locale: SupportedLocale,
): Option[] =>
  fetchedPrincipleConfigs.map((pc) => {
    if (pc.active) {
      return {
        label: pc.names[locale],
        value: pc.id,
      }
    }

    const inactiveLabel = getMessage('label.inactive')
    return {
      label: (pc.names[locale] || '') + ` (${inactiveLabel})`,
      value: pc.id,
      disabled: true,
    }
  })

export const fetchPrincipleConfigs = (urqlClient: Client) => async () => {
  const { data, error } = await urqlClient
    .query<{
      principle_config: Query_Root['principle_config']
    }>(queryAllPrincipleConfigs, {})
    .toPromise()

  if (error || !data?.principle_config) {
    throw new Error('Resource not found')
  }

  return {
    fetchedPrincipleConfigs: data.principle_config,
  }
}

export const insertSubprincipleMutation =
  (urqlClient: Client) => async (subprincipleInsertInput: Subprinciple_Config_Insert_Input) => {
    const { data, error } = await urqlClient
      .mutation<{
        insert_subprinciple_config: Mutation_Root['insert_subprinciple_config']
      }>(insertSubprincipleConfig, {
        subprincipleInsertInput: [subprincipleInsertInput],
      })
      .toPromise()

    if (error || data?.insert_subprinciple_config?.affected_rows !== 1) {
      throw new Error('Insert mutation failed to complete as expected')
    }
  }

export const updateSubprincipleMutation =
  (urqlClient: Client) => async (subprincipleId: number, subprincipleSetInput: Subprinciple_Config_Set_Input) => {
    const { data, error } = await urqlClient
      .mutation<{
        update_subprinciple_config: Mutation_Root['update_subprinciple_config']
      }>(updateSubprincipleConfigById, {
        subprincipleId,
        subprincipleSetInput,
      })
      .toPromise()

    if (error || data?.update_subprinciple_config?.affected_rows !== 1) {
      throw new Error('Update mutation failed to complete as expected')
    }
  }
